import type { Approval } from '@coworker/contracts';

import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { useApprovals, useDecideApproval } from '@/hooks/use-approvals.js';
import { errorText } from '@/lib/api-error.js';

/**
 * What the employee is waiting to be allowed to do - WEB-011, AGT-008.
 *
 * **The second place to answer, not the first.** A request appears in the chat
 * where it arose, next to what the employee was asked to do, and §14.3's
 * argument is that a person must see the action rather than "somebody wants
 * permission". This screen exists for the case that chat cannot serve: a
 * request raised in a thread you are not looking at, or one somebody else
 * started and you are being asked to answer.
 */
export function Approvals() {
  const { query } = useApprovals();
  const decide = useDecideApproval();

  return (
    <section className="space-y-4">
      <header className="space-y-1">
        <h1 className="text-xl font-semibold">Approvals</h1>
        <p className="text-sm text-muted-foreground">
          Things an employee has stopped to ask about. Answering one lets it carry on.
        </p>
      </header>

      {query.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {errorText(query.error)}
        </p>
      ) : null}

      {decide.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {errorText(decide.error)}
        </p>
      ) : null}

      {query.isPending ? (
        <p role="status" className="text-sm text-muted-foreground">
          Loading…
        </p>
      ) : null}

      {query.data !== undefined && query.data.approvals.length === 0 ? (
        <p role="status" className="text-sm text-muted-foreground">
          Nothing is waiting. An employee asks here when it needs permission to change something
          outside this workspace.
        </p>
      ) : null}

      <ul className="space-y-3">
        {(query.data?.approvals ?? []).map((approval) => (
          <ApprovalCard
            key={approval.id}
            approval={approval}
            busy={decide.isPending && decide.variables?.id === approval.id}
            onDecide={(decision) => decide.mutate({ id: approval.id, decision })}
          />
        ))}
      </ul>
    </section>
  );
}

export function ApprovalCard({
  approval,
  busy,
  onDecide,
}: {
  readonly approval: Approval;
  readonly busy: boolean;
  readonly onDecide: (decision: 'APPROVED' | 'REJECTED') => void;
}) {
  // **The list no longer carries expired requests** - `listPending` filters on
  // `expires_at`, which it did not, so this screen compensated client-side
  // while the chat card did not and kept two dead buttons. Kept as a second
  // guard for the seconds between a render and an expiry, not as the answer.
  //
  // **`Date.parse` is safe here only because the wire is ISO by construction.**
  // The value is a `timestamptz`, and Postgres's text form for one has an
  // offset while the Data API's is unmeasured; an offset-less string parses as
  // *local* time, which hid these buttons for the last two hours of a live
  // twenty-hour window. `isoTimestamp` in `packages/db/src/row.ts` normalises
  // at the decoder so no screen has to know which transport read the row.
  const expired = Date.parse(approval.expiresAt) <= Date.now();

  return (
    <li className="space-y-3 rounded-[--radius] border border-border p-4">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-medium">{approval.summary}</span>
        {/* The class, because "send an email" and "rename a label" are not the
            same question even when the sentence is the same length. */}
        <Badge variant="outline">{approval.sideEffect.toLowerCase().replace(/_/g, ' ')}</Badge>
      </div>

      <p className="text-xs text-muted-foreground">
        {expired
          ? 'This expired. Ask again if you still want it.'
          : `Expires ${approval.expiresAt}`}
      </p>

      {/* An expired request keeps its buttons hidden rather than disabled: the
          answer is not "wait", it is "this is over". */}
      {expired ? null : (
        <div className="flex gap-2">
          <Button type="button" disabled={busy} onClick={() => onDecide('APPROVED')}>
            Allow once
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => onDecide('REJECTED')}
          >
            Don&apos;t
          </Button>
        </div>
      )}
    </li>
  );
}
